import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { activeSteps } from "@/components/requests/stepDependencies";
import { useAuth } from "@/contexts/AuthContext";
import {
  COLLECTIONS,
  SUBCOLLECTIONS,
} from "@/integrations/firebase/collections";
import { db } from "@/integrations/firebase/firestore";
import { logError } from "@/shared/services/ErrorReporting";
import type { EvaluationStep, Request } from "./types";

export interface ResponsibleStepCount {
  /** Steps, not requests — see below. */
  count: number;
  /**
   * The requests those steps are on, so a caller can tell whether a *request*
   * is already counted here.
   *
   * "Waiting on you" carries one badge over both halves of somebody's work
   * (Jira NAV-111), and the two halves count different units: this one counts
   * steps and the access-decision queue counts requests. Adding them blind would
   * count a request twice if a step of it were also the reader's, so the
   * combined badge needs to know which requests this number is made of. Nothing
   * else can supply it: the count is a sum, and a sum cannot be asked what it
   * was summed from.
   */
  requestIds: readonly string[];
  /**
   * How many requests the steps tab would *list* — which is more than `count`
   * is made of, and is what decides whether the destination is offered at all.
   *
   * `count` answers "is work waiting on this person", and it is deliberately
   * narrower than the page: it drops a decided request (steps stay tickable
   * after the decision by design, but a settled request must stop nagging) and
   * a step whose dependency is still open. The page drops neither — it lists
   * every verified request the reader is a responsible on, which is
   * `useResponsibleRequests`' own filter. Gating the navigation entry on `count`
   * therefore hid the only route to a page that still had content on it, and
   * still took input (Jira NAV-111, reviewed on PR #100).
   */
  listedRequestCount: number;
  /**
   * True until `count` is settled — the requests query *and* every per-request
   * step listener it opened have answered at least once.
   *
   * Not just the query: the count is assembled from a listener per request, so
   * between the two a caller reading `count === 0` is reading "not yet", not
   * "nothing". The badge can live with that (it renders no badge either way);
   * the entry's *link target* cannot, since it flips under the click.
   */
  loading: boolean;
  /**
   * Whichever read failed, or null.
   *
   * Reported because every number above is a count, and a count has no way to
   * say it could not be taken: a refused query leaves `count` and
   * `listedRequestCount` at zero with `loading` false, which reads as "no work
   * is waiting on you". For a badge that is harmless. For the navigation entry
   * this feeds, it is not — the entry is *omitted* at zero, so a failed read
   * removed the only route to the queue and said nothing.
   */
  error: Error | null;
}

/**
 * How many evaluation steps are waiting on the signed-in user right now — half
 * of the badge on "Waiting on you" (PRD D6).
 *
 * Counts steps, not requests: one request can hold several of your steps, and a
 * request where your step is already done is not waiting on you. Only PENDING
 * requests count — steps stay tickable after the decision by design (HBV has
 * post-decision steps), but a decided request must stop nagging. A step that
 * has not started — its declared dependencies are still open (PRD D6) — is not
 * waiting on you either: it is on the page, marked, but a badge that counts it
 * asks for work nobody can do yet.
 *
 * There is no cheaper shape for this. A collection-group query over `steps`
 * would need its own rule (the nested `requests/{id}/steps/{id}` rule does not
 * authorize one), a composite index, and the responsible's uid denormalised
 * onto each step — a lot of moving parts for a number. So this mirrors what the
 * page does: one query for the requests you are responsible on, then a listener
 * per request. That is a handful of listeners for a handful of documents; it is
 * mounted app-wide (the user dropdown), which is why it returns a count and
 * little else — the "little else" being two facts the count cannot answer for
 * itself: which requests it is made of, and how many the page would list even
 * where none of them are waiting on anybody (see the fields).
 */
export function useResponsibleStepCount(): ResponsibleStepCount {
  // Identity-scoped read: whose approvals to show. Follows the
  // impersonation target like the other 'my …' surfaces — the `requests`
  // read rule admits a super admin, so nothing blocks it.
  const { actingUid } = useAuth();
  // The request ids held as a joined string, not an array: the listeners below
  // must be rebuilt when the *set* changes and not on every snapshot of an
  // unchanged set, and a string compares by value.
  const [requestIdKey, setRequestIdKey] = useState("");
  const [listedRequestCount, setListedRequestCount] = useState(0);
  const [countsByRequest, setCountsByRequest] = useState<
    Record<string, number>
  >({});
  const [queryLoading, setQueryLoading] = useState(true);
  // Either read failing makes every count below meaningless — see `error` on
  // the result type. **Two states for two writers, because each has to clear
  // exactly what it wrote.** The requests query resets on `actingUid` and the
  // steps listeners reset on the set of requests they are open over, and those
  // are different keys: one state would either outlive the subscription that
  // set it or race the other writer's error on mount. First one wins within
  // each; which read it was is the error code's job.
  const [queryError, setQueryError] = useState<Error | null>(null);
  const [stepsError, setStepsError] = useState<Error | null>(null);

  useEffect(() => {
    // Reset before re-subscribing — see the note in `useRequests`. A stale
    // error outlives the subscription that set it, so a *successful*
    // snapshot repopulates the data while the consumer still renders the
    // failure, until a full reload. This hook is keyed on `actingUid`.
    setQueryError(null);
    if (!actingUid) {
      setRequestIdKey("");
      setListedRequestCount(0);
      setCountsByRequest({});
      setQueryLoading(false);
      return;
    }
    const unsubscribe = onSnapshot(
      query(
        collection(db, COLLECTIONS.REQUESTS),
        where("responsibleUserIds", "array-contains", actingUid),
      ),
      (snapshot) => {
        // One snapshot, two answers, because they are two different questions.
        // Status is filtered here rather than in the query: pairing
        // array-contains with an equality filter would need a composite index
        // for a set this small.
        const listed = snapshot.docs.filter(
          // What the page shows — `useResponsibleRequests`' filter, to the
          // letter. An unverified request has not reached the responsible yet.
          (doc) => (doc.data() as Request).subjectVerifiedAt != null,
        );
        setListedRequestCount(listed.length);
        setRequestIdKey(
          listed
            // ...and of those, the ones worth counting: a decided request must
            // stop nagging, though its steps stay tickable and stay on the page.
            .filter((doc) => (doc.data() as Request).status === "PENDING")
            .map((doc) => doc.id)
            .sort()
            .join(","),
        );
        setQueryLoading(false);
      },
      (refusal) => {
        logError(refusal);
        setQueryError(refusal);
        setQueryLoading(false);
      },
    );
    return () => unsubscribe();
  }, [actingUid]);

  const requestIds = requestIdKey ? requestIdKey.split(",") : [];

  useEffect(() => {
    // The same reset, for this effect's own writer. A refusal on one request's
    // steps belongs to the subscription that saw it: once the set of requests
    // changes — a decision lands, or the identity does — fresh listeners answer
    // for every count, and a failure held past that suppresses the "waiting on
    // you" entry over numbers that are since correct.
    setStepsError(null);
    const ids = requestIdKey ? requestIdKey.split(",") : [];
    if (!actingUid || ids.length === 0) {
      setCountsByRequest({});
      return;
    }
    const unsubscribes = ids.map((requestId) =>
      onSnapshot(
        collection(db, COLLECTIONS.REQUESTS, requestId, SUBCOLLECTIONS.STEPS),
        (snapshot) => {
          // Derived over the whole subcollection, not doc by doc: whether a
          // step has started depends on the steps it declared a wait for
          // (PRD D6), which are usually somebody else's.
          const mine = activeSteps(
            snapshot.docs.map(
              (doc) => ({ ...doc.data(), id: doc.id }) as EvaluationStep,
            ),
          ).filter((step) => step.responsible?.userId === actingUid).length;
          setCountsByRequest((current) =>
            current[requestId] === mine
              ? current
              : { ...current, [requestId]: mine },
          );
        },
        (refusal) => {
          logError(refusal);
          // **Reported, because a count cannot say it is short.** This is a
          // different collection and a different rule from the query above, so
          // it is independently refusable — and a zero recorded silently left
          // `count` short with `error` null, which reads as "no work is waiting
          // on you". The nav entry derived from it then showed a confident badge
          // of the decisions alone and pointed at the wrong tab, so the reader
          // following a reminder never reached the notice saying the read
          // failed. First one wins, matching the requests query above.
          setStepsError((current) => current ?? refusal);
          // Still a zero rather than silence: a listener that never answers
          // would leave `loading` true for ever, and the caller waits on it
          // before deciding where the entry points.
          setCountsByRequest((current) =>
            current[requestId] === 0 ? current : { ...current, [requestId]: 0 },
          );
        },
      ),
    );
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [requestIdKey, actingUid]);

  // Only the current set counts: a request that has just been decided may still
  // have a stale entry in the map for a tick.
  const live = new Set(requestIds);
  const counted = Object.entries(countsByRequest).filter(
    ([requestId, value]) => live.has(requestId) && value > 0,
  );
  const count = counted.reduce((total, [, value]) => total + value, 0);

  return {
    count,
    // The requests this number is actually made of — the ones that contributed
    // a step, not every request the reader is a responsible on. A request whose
    // steps are all done or all still waiting adds nothing to `count`, so
    // reporting it here would let the combined badge subtract a request nothing
    // had counted.
    requestIds: counted.map(([requestId]) => requestId),
    listedRequestCount,
    // Settled once every listener the current set opened has reported. A first
    // snapshot always writes its request's key — even a zero, since an absent
    // key is not 0 — so "has a key" is "has answered".
    loading: queryLoading || !requestIds.every((id) => id in countsByRequest),
    error: queryError ?? stepsError,
  };
}
